/**
 * The dashboards the rail lists under Home, in two named sections.
 *
 * PURE, AND IN ITS OWN FILE so it can be tested: the Sidebar imports MUI, react-router and the
 * session, and a function that decides which name goes under which heading has nothing to do with
 * any of them. The same split `chartSpec` made away from `dashboardApi`.
 *
 * GROUPED BY WHO WROTE IT, NOT BY WHETHER IT IS PRIVATE. The two look the same until somebody
 * publishes a dashboard they made, and then they disagree: under a visibility split that dashboard
 * leaves "My Dashboards" the moment it becomes useful to anybody else, so the question people
 * actually ask -- "where is the one I built last week" -- stops having an answer. Private is a
 * PROPERTY of one of your dashboards, shown as a suffix, rather than the thing that decides where
 * it is filed. Nothing is lost by this: the list a reader gets back holds every public dashboard
 * plus their own private ones, so a private one under somebody else's name cannot arise.
 */

/** What this needs from a dashboard summary, structurally -- so the test need not import the API
 *  client, which drags axios in and Jest cannot parse it. */
export type RailDashboard = {
    USID: string;
    Name: string;
    IsPrivate?: boolean;
    Mine?: boolean;
};

export type RailItem = {
    label: string;
    route: string;
    /** A section name. Not a destination: it has no route and does not respond to a click. */
    heading?: boolean;
};

/** The suffix a private dashboard carries. Its author is the only person who can see the row at
 *  all, so this says "nobody else sees this" rather than "you may not open it". */
const PRIVATE = " · private";

export function dashboardRailItems(dashboards: RailDashboard[]): RailItem[] {
    const byName = (a: RailDashboard, b: RailDashboard) =>
        (a.Name || "").toLowerCase().localeCompare((b.Name || "").toLowerCase());

    const theirs = dashboards.filter(one => !one.Mine).sort(byName);
    const mine = dashboards.filter(one => one.Mine).sort(byName);

    const out: RailItem[] = [];
    // A HEADING ONLY WHEN IT NAMES SOMETHING. An empty "My Dashboards" tells a new reader the rail
    // is broken; no heading tells them, correctly, that they have not made one yet.
    if (theirs.length > 0) {
        out.push({label: "Account", route: "", heading: true});
        for (const one of theirs) out.push({label: one.Name, route: `/dashboards/${one.USID}`});
    }
    if (mine.length > 0) {
        out.push({label: "My Dashboards", route: "", heading: true});
        for (const one of mine) {
            out.push({label: one.Name + (one.IsPrivate ? PRIVATE : ""),
                      route: `/dashboards/${one.USID}`});
        }
    }
    return out;
}
